import React, { useEffect, useMemo, useRef, useState } from "react";

import { Link, useParams, useSearchParams } from "react-router-dom";
import { PROPERTIES, type GalleryCategoryKey } from "../data/properties";
import Badge from "../components/Badge";
import GalleryViewer from "../components/GalleryViewer";
import { useTheme } from "../lib/theme";

type GalleryOption = {
    key: GalleryCategoryKey;
    label: string;
};

const GALLERY_OPTIONS_ALL: GalleryOption[] = [
    { key: "aerial", label: "Aerial" },
    { key: "featured", label: "Featured" },
    { key: "indoors", label: "Indoors" },
    { key: "wildlife", label: "Wildlife" },
    { key: "water", label: "Water" },
    { key: "videos", label: "Videos" },
    { key: "other", label: "Other" },
];

function toImageUrl(shortId: string, file: string): string {
    if (file.startsWith("/")) return file;
    return `/static/properties/${shortId}/images/${file}`;
}

function toVideoUrl(shortId: string, file: string): string {
    if (file.startsWith("/")) return file;
    return `/static/properties/${shortId}/videos/${file}`;
}

function toPosterUrl(videoUrl: string): string {
    const [base, q] = videoUrl.split("?");
    const posterBase = (base ?? videoUrl).replace(/\.mp4$/i, ".poster.jpg");
    return q ? `${posterBase}?${q}` : posterBase;
}

async function requestVideoFullscreen(
    videoEl: HTMLVideoElement,
    containerEl: HTMLElement | null,
): Promise<void> {
    const anyVideo = videoEl as unknown as {
        webkitEnterFullscreen?: () => void;
        requestFullscreen?: () => Promise<void>;
    };

    if (typeof anyVideo.webkitEnterFullscreen === "function") {
        anyVideo.webkitEnterFullscreen();
        return;
    }

    if (typeof anyVideo.requestFullscreen === "function") {
        await anyVideo.requestFullscreen();
        return;
    }

    const anyContainer = containerEl as unknown as {
        requestFullscreen?: () => Promise<void>;
    };

    if (containerEl && typeof anyContainer.requestFullscreen === "function") {
        await anyContainer.requestFullscreen();
    }
}

async function exitAnyFullscreen(
    videoEl: HTMLVideoElement | null,
): Promise<void> {
    const d = document as unknown as {
        fullscreenElement?: Element | null;
        exitFullscreen?: () => Promise<void>;
        webkitExitFullscreen?: () => Promise<void>;
    };

    if (typeof d.exitFullscreen === "function" && d.fullscreenElement) {
        await d.exitFullscreen();
        return;
    }

    if (typeof d.webkitExitFullscreen === "function") {
        await d.webkitExitFullscreen();
        return;
    }

    const anyVideo = videoEl as unknown as {
        webkitExitFullscreen?: () => void;
        webkitDisplayingFullscreen?: boolean;
    };

    if (videoEl && typeof anyVideo.webkitExitFullscreen === "function") {
        anyVideo.webkitExitFullscreen();
        return;
    }

    if (videoEl && anyVideo.webkitDisplayingFullscreen) {
        return;
    }
}

type MediaType = "image" | "video";

type MediaItem = {
    category: GalleryCategoryKey;
    type: MediaType;
    file: string;
    url: string;
    label: string;
};

function prettyMediaLabel(fileOrUrl: string): string {
    const rawName = (() => {
        const s = fileOrUrl.split("?")[0] ?? fileOrUrl;
        const parts = s.split("/");
        return parts[parts.length - 1] ?? s;
    })();

    const noExt = rawName.replace(/\.[^/.]+$/, "");
    const spaced = noExt.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

    const titled = spaced
        .split(" ")
        .filter(Boolean)
        .map((w) => w.slice(0, 1).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

    return titled || noExt || rawName;
}

export interface PropertyDetailPageProps {
    standalone?: boolean;
}

type LabelInset = {
    left: number;
    top: number;
};

const PropertyDetailPage: React.FC<PropertyDetailPageProps> = ({
    standalone = false,
}) => {
    const [searchParams] = useSearchParams();
    const params = useParams<{ id?: string; shortId?: string }>();
    const property = useMemo(
        () =>
            PROPERTIES.find((p) => {
                if (params.shortId) {
                    return p.shortId === params.shortId;
                } else if (params.id) {
                    return p.slug === params.id || p.shortId === params.id;
                }
                return false;
            }),
        [params.id, params.shortId],
    );
    const isStandalone =
        standalone || searchParams.get("mode") === "standalone";

    const [viewerOpen, setViewerOpen] = useState<boolean>(false);
    const [viewerIndex, setViewerIndex] = useState<number>(0);

    const { setOverrideTheme } = useTheme();

    useEffect(() => {
        if (!property) return;
        setOverrideTheme(property.theme ?? null);
        return () => {
            setOverrideTheme(null);
        };
    }, [property, setOverrideTheme]);

    const [galleryCategory, setGalleryCategory] =
        useState<GalleryCategoryKey>("featured");

    useEffect(() => {
        if (!property) return;

        const cat = property.galleries ?? null;

        const hasHeros =
            (cat?.featured?.length ?? 0) > 0 || Boolean(property.image);

        const availableKeys: GalleryCategoryKey[] = GALLERY_OPTIONS_ALL.map(
            (o) => o.key,
        ).filter((k) => {
            if (k === "featured") return hasHeros;
            const arr = cat?.[k];
            return Array.isArray(arr) && arr.length > 0;
        });

        const defaultKey: GalleryCategoryKey = availableKeys.includes(
            "featured",
        )
            ? "featured"
            : (availableKeys[0] ?? "featured");

        setGalleryCategory(defaultKey);
        setViewerOpen(false);
        setViewerIndex(0);
    }, [property]);

    const categoryButtonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoWrapRef = useRef<HTMLDivElement | null>(null);

    const imageWrapRef = useRef<HTMLDivElement | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    const [labelInset, setLabelInset] = useState<LabelInset>({
        left: 14,
        top: 14,
    });

    const videoSkipArmedRef = useRef<boolean>(false);
    const videoSkipPendingPlayRef = useRef<boolean>(false);

    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    useEffect(() => {
        const onFsChange = () => {
            const d = document as unknown as {
                fullscreenElement?: Element | null;
            };
            const fs = Boolean(d.fullscreenElement);
            setIsFullscreen(fs);
        };

        document.addEventListener("fullscreenchange", onFsChange);
        return () => {
            document.removeEventListener("fullscreenchange", onFsChange);
        };
    }, []);

    useEffect(() => {
        if (!menuOpen) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setMenuOpen(false);
                categoryButtonRef.current?.focus();
            }
        };

        const onPointerDown = (e: PointerEvent) => {
            const t = e.target as Node | null;
            if (!t) return;

            const btn = categoryButtonRef.current;
            const menu = menuRef.current;

            if (btn && btn.contains(t)) return;
            if (menu && menu.contains(t)) return;

            setMenuOpen(false);
        };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("pointerdown", onPointerDown);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("pointerdown", onPointerDown);
        };
    }, [menuOpen]);

    if (!property) {
        return (
            <div className="space-y-4">
                <div className="text-sm text-[rgb(var(--muted))]">
                    Property not found.
                </div>
                {!isStandalone && (
                    <Link
                        to="/properties"
                        className="inline-flex items-center justify-center rounded-2xl border border-[rgb(var(--border)/0.15)] bg-[rgb(var(--surface)/0.05)] px-4 py-3 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgb(var(--surface)/0.10)]"
                    >
                        Back to properties
                    </Link>
                )}
            </div>
        );
    }

    const imageUrl = toImageUrl(property.shortId, property.image);

    const categoryOptions: GalleryOption[] = (() => {
        const cat = property.galleries ?? null;

        const hasHeros =
            (cat?.featured?.length ?? 0) > 0 || Boolean(property.image);

        return GALLERY_OPTIONS_ALL.filter((o) => {
            if (o.key === "featured") return hasHeros;
            const arr = cat?.[o.key];
            return Array.isArray(arr) && arr.length > 0;
        });
    })();

    const showCategoryMenu: boolean = (() => {
        if (categoryOptions.length <= 1) return false;
        if (
            categoryOptions.length === 1 &&
            categoryOptions[0]?.key === "featured"
        )
            return false;
        if (categoryOptions.every((o) => o.key === "featured")) return false;
        return true;
    })();

    const categoryLabel: string =
        categoryOptions.find((o) => o.key === galleryCategory)?.label ??
        categoryOptions[0]?.label ??
        "Featured";

    const categoryItems: MediaItem[] = (() => {
        const cat = property.galleries ?? null;

        if (galleryCategory === "featured") {
            const herosRaw: string[] = cat?.featured ?? [];
            const featured: string[] = [
                property.image,
                ...herosRaw.filter((f) => f !== property.image),
            ].filter(Boolean);

            const unique: string[] = [];
            for (const f of featured) {
                if (!unique.includes(f)) unique.push(f);
            }

            return unique.map((f) => ({
                category: "featured",
                type: "image",
                file: f,
                url: toImageUrl(property.shortId, f),
                label: prettyMediaLabel(f),
            }));
        }

        const arr = cat?.[galleryCategory] ?? [];

        if (galleryCategory === "videos") {
            return arr.map((f) => ({
                category: "videos",
                type: "video",
                file: f,
                url: toVideoUrl(property.shortId, f),
                label: prettyMediaLabel(f),
            }));
        }

        return arr.map((f) => ({
            category: galleryCategory,
            type: "image",
            file: f,
            url: toImageUrl(property.shortId, f),
            label: prettyMediaLabel(f),
        }));
    })();

    const currentUrls: string[] = categoryItems.map((x) => x.url);

    const isVideos: boolean = galleryCategory === "videos";

    const mainUrl: string =
        currentUrls[viewerIndex] ?? currentUrls[0] ?? imageUrl;

    const thumbUrls: string[] = (() => {
        if (currentUrls.length <= 1) return [];
        if (isVideos) {
            return currentUrls.filter((_, i) => i !== viewerIndex).slice(0, 3);
        }
        return currentUrls.slice(1, 4);
    })();

    const allMedia: MediaItem[] = useMemo(() => {
        const cat = property.galleries ?? null;

        const buildHeros = (): MediaItem[] => {
            const herosRaw: string[] = cat?.featured ?? [];
            const featured: string[] = [
                property.image,
                ...herosRaw.filter((f) => f !== property.image),
            ].filter(Boolean);

            const unique: string[] = [];
            for (const f of featured) {
                if (!unique.includes(f)) unique.push(f);
            }

            return unique.map((f) => ({
                category: "featured",
                type: "image",
                file: f,
                url: toImageUrl(property.shortId, f),
                label: prettyMediaLabel(f),
            }));
        };

        const buildImages = (
            k: Exclude<GalleryCategoryKey, "videos" | "featured">,
        ): MediaItem[] => {
            const arr = (cat?.[k] ?? []) as string[];
            return arr.map((f) => ({
                category: k,
                type: "image",
                file: f,
                url: toImageUrl(property.shortId, f),
                label: prettyMediaLabel(f),
            }));
        };

        const buildVideos = (): MediaItem[] => {
            const arr = (cat?.videos ?? []) as string[];
            return arr.map((f) => ({
                category: "videos",
                type: "video",
                file: f,
                url: toVideoUrl(property.shortId, f),
                label: prettyMediaLabel(f),
            }));
        };

        const out: MediaItem[] = [];

        const hasHeros =
            (cat?.featured?.length ?? 0) > 0 || Boolean(property.image);

        for (const opt of GALLERY_OPTIONS_ALL) {
            if (opt.key === "featured") {
                if (hasHeros) out.push(...buildHeros());
                continue;
            }

            if (opt.key === "videos") {
                if ((cat?.videos?.length ?? 0) > 0) out.push(...buildVideos());
                continue;
            }

            const arr = cat?.[opt.key] as unknown;
            if (Array.isArray(arr) && arr.length > 0) {
                out.push(
                    ...buildImages(
                        opt.key as Exclude<
                            GalleryCategoryKey,
                            "videos" | "featured"
                        >,
                    ),
                );
            }
        }

        return out;
    }, [property]);

    const [globalIndex, setGlobalIndex] = useState<number>(0);

    useEffect(() => {
        if (allMedia.length === 0) {
            setGlobalIndex(0);
            return;
        }

        const inCategory = allMedia.filter(
            (m) => m.category === galleryCategory,
        );
        const currentItem = inCategory[viewerIndex] ?? inCategory[0] ?? null;

        if (!currentItem) {
            setGlobalIndex(0);
            return;
        }

        const idx = allMedia.findIndex(
            (m) =>
                m.category === currentItem.category &&
                m.url === currentItem.url,
        );

        setGlobalIndex(idx >= 0 ? idx : 0);
    }, [allMedia, galleryCategory, viewerIndex]);

    const syncToGlobalIndex = (nextGlobalIndex: number): void => {
        if (allMedia.length === 0) return;

        const clamped =
            ((nextGlobalIndex % allMedia.length) + allMedia.length) %
            allMedia.length;

        const item = allMedia[clamped];
        if (!item) return;

        const inCategory = allMedia.filter((m) => m.category === item.category);
        const idxInCategory = inCategory.findIndex((m) => m.url === item.url);

        setMenuOpen(false);
        setViewerOpen(false);
        setGalleryCategory(item.category);
        setViewerIndex(idxInCategory >= 0 ? idxInCategory : 0);
        setGlobalIndex(clamped);
    };

    const canCycle: boolean = allMedia.length > 1;

    const goPrev = (): void => {
        if (!canCycle) return;
        syncToGlobalIndex(globalIndex - 1);
    };

    const goNext = (): void => {
        if (!canCycle) return;
        syncToGlobalIndex(globalIndex + 1);
    };

    const touchStartXRef = useRef<number | null>(null);

    const onTouchStart = (e: React.TouchEvent): void => {
        if (!canCycle) return;
        const t = e.touches?.[0];
        if (!t) return;
        touchStartXRef.current = t.clientX;
    };

    const onTouchEnd = (e: React.TouchEvent): void => {
        if (!canCycle) return;
        const startX = touchStartXRef.current;
        touchStartXRef.current = null;
        if (typeof startX !== "number") return;

        const t = e.changedTouches?.[0];
        if (!t) return;

        const dx = t.clientX - startX;

        if (Math.abs(dx) < 50) return;

        if (dx < 0) goNext();
        else goPrev();
    };

    const activeItem: MediaItem | null = allMedia[globalIndex] ?? null;
    const activeLabel: string =
        activeItem?.label ||
        categoryItems[viewerIndex]?.label ||
        categoryItems[0]?.label ||
        prettyMediaLabel(mainUrl);

    const keyDetailsRows: Array<{ label: string; value: string }> = (() => {
        const rows: Array<{ label: string; value: string }> = [];

        if (property.kind === "residential") {
            rows.push({ label: "Beds", value: String(property.beds) });
            rows.push({ label: "Baths", value: String(property.baths) });
            rows.push({ label: "Sqft", value: property.sqft.toLocaleString() });
        } else if (property.kind === "improved_land") {
            if (typeof property.beds === "number")
                rows.push({ label: "Beds", value: String(property.beds) });
            if (typeof property.baths === "number")
                rows.push({ label: "Baths", value: String(property.baths) });
            if (typeof property.sqft === "number")
                rows.push({
                    label: "Sqft",
                    value: property.sqft.toLocaleString(),
                });
        }

        if (typeof property.acres === "number") {
            rows.push({ label: "Acres", value: String(property.acres) });
        }

        rows.push({ label: "Status", value: property.status });

        return rows;
    })();

    const arrowBtnClass: string =
        "pointer-events-auto inline-flex items-center justify-center " +
        "rounded-2xl border border-[rgb(var(--border)/0.15)] " +
        "bg-[rgb(var(--accent)/0.08)] text-[rgb(var(--accent-fg))] " +
        "shadow-sm backdrop-blur-md transition " +
        "hover:bg-[rgb(var(--accent)/0.14)] active:scale-[0.985] " +
        "sm:border-[rgb(var(--border)/0.22)] " +
        "sm:bg-[rgb(var(--surface)/0.28)] sm:hover:bg-[rgb(var(--surface)/0.38)]";

    const HeroArrowButton = ({
        label,
        onClick,
        side,
        direction,
    }: {
        label: string;
        onClick: (e: React.MouseEvent) => void;
        side: "left" | "right";
        direction: "prev" | "next";
    }): JSX.Element => {
        const pos = side === "left" ? "left-3" : "right-3";
        const path = direction === "prev" ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6";

        return (
            <button
                type="button"
                aria-label={label}
                className={`absolute ${pos} top-1/2 -translate-y-1/2 ${arrowBtnClass} hidden sm:inline-flex`}
                style={{ width: 44, height: 64 }}
                onClick={onClick}
            >
                <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    aria-hidden="true"
                    className="drop-shadow-sm"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d={path} />
                </svg>
            </button>
        );
    };

    const remainingThumbs = currentUrls.length - thumbUrls.length - 1;

    const computeContainInset = (
        containerW: number,
        containerH: number,
        mediaW: number,
        mediaH: number,
        pad: number,
    ): LabelInset => {
        if (
            !Number.isFinite(containerW) ||
            !Number.isFinite(containerH) ||
            !Number.isFinite(mediaW) ||
            !Number.isFinite(mediaH) ||
            containerW <= 0 ||
            containerH <= 0 ||
            mediaW <= 0 ||
            mediaH <= 0
        ) {
            return { left: pad, top: pad };
        }

        const scale = Math.min(containerW / mediaW, containerH / mediaH);
        const dispW = mediaW * scale;
        const dispH = mediaH * scale;

        const offX = (containerW - dispW) / 2;
        const offY = (containerH - dispH) / 2;

        return {
            left: Math.max(pad, offX + pad),
            top: Math.max(pad, offY + pad),
        };
    };

    const updateLabelInset = (): void => {
        const pad: number = 10;

        if (isVideos) {
            const wrap = videoWrapRef.current;
            const v = videoRef.current;
            if (!wrap || !v) {
                setLabelInset({ left: pad, top: pad });
                return;
            }

            const cw = wrap.clientWidth;
            const ch = wrap.clientHeight;

            const mw = v.videoWidth;
            const mh = v.videoHeight;

            setLabelInset(computeContainInset(cw, ch, mw, mh, pad));
            return;
        }

        const wrap = imageWrapRef.current;
        const img = imageRef.current;

        if (!wrap || !img) {
            setLabelInset({ left: pad, top: pad });
            return;
        }

        const cw = wrap.clientWidth;
        const ch = wrap.clientHeight;

        const mw = img.naturalWidth;
        const mh = img.naturalHeight;

        setLabelInset(computeContainInset(cw, ch, mw, mh, pad));
    };

    useEffect(() => {
        updateLabelInset();

        const wrap = (
            isVideos ? videoWrapRef.current : imageWrapRef.current
        ) as HTMLDivElement | null;

        if (!wrap) return;

        const ro = new ResizeObserver(() => {
            updateLabelInset();
        });

        ro.observe(wrap);

        return () => {
            ro.disconnect();
        };
    }, [isVideos, mainUrl]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-1">
                    {!isStandalone && (
                        <div className="text-sm text-[rgb(var(--muted))]">
                            <Link
                                className="hover:text-[rgb(var(--fg))]"
                                to="/properties"
                            >
                                Properties
                            </Link>
                            <span className="mx-2 text-[rgb(var(--muted3))]">
                                /
                            </span>
                            <span className="text-[rgb(var(--muted2))]">
                                {property.title}
                            </span>
                        </div>
                    )}
                    <h1 className="text-2xl font-semibold tracking-tight">
                        {property.title}
                    </h1>
                    <div className="text-sm text-[rgb(var(--muted))]">
                        {property.location}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <span className="rounded-full border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] px-3 py-2 text-sm font-semibold">
                        {property.priceLabel}
                    </span>
                    {!isStandalone && (
                        <Link
                            to="/contact"
                            state={{ message: `RE: ${property.title}` }}
                            className="inline-flex items-center justify-center rounded-2xl bg-[rgb(var(--accent))] px-4 py-3 text-sm font-semibold text-[rgb(var(--accent-fg))] shadow-sm transition hover:opacity-90"
                        >
                            Request info
                        </Link>
                    )}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
                <div className="space-y-4 lg:col-span-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold">Gallery</div>

                        {showCategoryMenu ? (
                            <div className="relative z-50">
                                <button
                                    ref={categoryButtonRef}
                                    type="button"
                                    className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-3 py-2 text-sm text-[rgb(var(--fg))] outline-none transition hover:bg-[rgb(var(--panel)/0.60)] focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                                    aria-haspopup="listbox"
                                    aria-expanded={menuOpen}
                                    onClick={() => setMenuOpen((v) => !v)}
                                >
                                    <span className="whitespace-nowrap">
                                        {categoryLabel}
                                    </span>
                                    <span className="text-[rgb(var(--muted))]">
                                        ▾
                                    </span>
                                </button>

                                {menuOpen ? (
                                    <div
                                        ref={menuRef}
                                        role="listbox"
                                        aria-label="Gallery categories"
                                        className="absolute right-0 z-[60] mt-2 w-48 overflow-hidden rounded-2xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.92)] shadow-lg backdrop-blur"
                                    >
                                        <div className="p-1">
                                            {categoryOptions.map((opt) => {
                                                const active =
                                                    opt.key === galleryCategory;
                                                return (
                                                    <button
                                                        key={opt.key}
                                                        type="button"
                                                        role="option"
                                                        aria-selected={active}
                                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition ${
                                                            active
                                                                ? "bg-[rgb(var(--surface)/0.10)] text-[rgb(var(--fg))]"
                                                                : "text-[rgb(var(--muted2))] hover:bg-[rgb(var(--surface)/0.07)] hover:text-[rgb(var(--fg))]"
                                                        }`}
                                                        onClick={() => {
                                                            const nextKey =
                                                                opt.key;

                                                            setGalleryCategory(
                                                                nextKey,
                                                            );
                                                            setViewerOpen(
                                                                false,
                                                            );
                                                            setViewerIndex(0);
                                                            setMenuOpen(false);
                                                            categoryButtonRef.current?.focus();

                                                            const firstOfCat =
                                                                allMedia.findIndex(
                                                                    (m) =>
                                                                        m.category ===
                                                                        nextKey,
                                                                );

                                                            if (
                                                                firstOfCat >= 0
                                                            ) {
                                                                setGlobalIndex(
                                                                    firstOfCat,
                                                                );
                                                            } else {
                                                                setGlobalIndex(
                                                                    0,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {active ? (
                                                            <span className="text-[rgb(var(--accent))]">
                                                                ●
                                                            </span>
                                                        ) : (
                                                            <span className="text-[rgb(var(--muted3))]">
                                                                {" "}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    <div
                        className="overflow-hidden rounded-3xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.40)]"
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                    >
                        {isVideos ? (
                            <div ref={videoWrapRef} className="relative">
                                <video
                                    key={mainUrl}
                                    ref={videoRef}
                                    src={mainUrl}
                                    poster={toPosterUrl(mainUrl)}
                                    controls
                                    playsInline
                                    preload="auto"
                                    className="h-64 w-full bg-black sm:h-80"
                                    onLoadedMetadata={(e) => {
                                        const v = e.currentTarget;
                                        videoSkipArmedRef.current = true;
                                        videoSkipPendingPlayRef.current = false;

                                        const skipSeconds: number = 0.2;
                                        if (
                                            Number.isFinite(v.duration) &&
                                            v.duration > skipSeconds
                                        ) {
                                            v.currentTime = skipSeconds;
                                        }

                                        updateLabelInset();
                                    }}
                                    onPlay={(e) => {
                                        const v = e.currentTarget;

                                        const skipSeconds: number = 0.2;

                                        if (!videoSkipArmedRef.current) return;
                                        if (
                                            v.currentTime >=
                                            skipSeconds - 0.001
                                        )
                                            return;

                                        v.pause();
                                        videoSkipPendingPlayRef.current = true;

                                        if (
                                            Number.isFinite(v.duration) &&
                                            v.duration > skipSeconds
                                        ) {
                                            v.currentTime = skipSeconds;
                                        }
                                    }}
                                    onSeeked={(e) => {
                                        const v = e.currentTarget;
                                        if (!videoSkipPendingPlayRef.current)
                                            return;

                                        videoSkipPendingPlayRef.current = false;
                                        void v.play();
                                    }}
                                />

                                <div
                                    className="pointer-events-none absolute"
                                    style={{
                                        left: labelInset.left,
                                        top: labelInset.top,
                                    }}
                                >
                                    <div className="w-fit max-w-[85vw] truncate rounded-2xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.55)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm backdrop-blur">
                                        {activeLabel}
                                    </div>
                                </div>

                                {canCycle ? (
                                    <>
                                        <HeroArrowButton
                                            label="Previous"
                                            side="left"
                                            direction="prev"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                goPrev();
                                            }}
                                        />
                                        <HeroArrowButton
                                            label="Next"
                                            side="right"
                                            direction="next"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                goNext();
                                            }}
                                        />
                                    </>
                                ) : null}

                                <div className="absolute right-3 top-3 flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center rounded-xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.55)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm backdrop-blur transition hover:bg-[rgb(var(--panel)/0.70)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                                        onClick={async () => {
                                            if (!videoRef.current) return;
                                            await requestVideoFullscreen(
                                                videoRef.current,
                                                videoWrapRef.current,
                                            );
                                        }}
                                    >
                                        Full screen
                                    </button>

                                    {isFullscreen ? (
                                        <button
                                            type="button"
                                            className="inline-flex items-center justify-center rounded-xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.55)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm backdrop-blur transition hover:bg-[rgb(var(--panel)/0.70)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                                            onClick={async () => {
                                                await exitAnyFullscreen(
                                                    videoRef.current,
                                                );
                                            }}
                                        >
                                            Exit
                                        </button>
                                    ) : null}
                                </div>

                                {isFullscreen ? (
                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 p-3">
                                        <div className="mx-auto w-fit rounded-2xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.55)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm backdrop-blur">
                                            Tip: use your phone’s Back
                                            gesture/button
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div ref={imageWrapRef} className="relative">
                                <img
                                    ref={imageRef}
                                    src={mainUrl}
                                    alt={property.title}
                                    onClick={() => {
                                        setViewerIndex(0);
                                        setViewerOpen(true);
                                    }}
                                    onLoad={() => {
                                        updateLabelInset();
                                    }}
                                    className="h-64 w-full cursor-pointer object-contain bg-black sm:h-80"
                                    width={1600}
                                    height={900}
                                />

                                <div
                                    className="pointer-events-none absolute"
                                    style={{
                                        left: labelInset.left,
                                        top: labelInset.top,
                                    }}
                                >
                                    <div className="w-fit max-w-[85vw] truncate rounded-2xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.55)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm backdrop-blur">
                                        {activeLabel}
                                    </div>
                                </div>

                                {canCycle ? (
                                    <>
                                        <HeroArrowButton
                                            label="Previous"
                                            side="left"
                                            direction="prev"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                goPrev();
                                            }}
                                        />
                                        <HeroArrowButton
                                            label="Next"
                                            side="right"
                                            direction="next"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                goNext();
                                            }}
                                        />
                                    </>
                                ) : null}
                            </div>
                        )}
                    </div>

                    {thumbUrls.length > 0 ? (
                        <div className="grid grid-cols-3 gap-3">
                            {thumbUrls.map((src, i) => (
                                <div
                                    key={src}
                                    className="overflow-hidden rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.40)] relative"
                                >
                                    {isVideos ? (
                                        <video
                                            key={src}
                                            src={src}
                                            poster={toPosterUrl(src)}
                                            muted
                                            playsInline
                                            preload="metadata"
                                            onClick={() => {
                                                const idx =
                                                    currentUrls.indexOf(src);
                                                if (idx >= 0) {
                                                    setViewerIndex(idx);
                                                }
                                            }}
                                            className="h-24 w-full cursor-pointer object-cover bg-black sm:h-28"
                                        />
                                    ) : (
                                        <img
                                            src={src}
                                            alt={property.title}
                                            onClick={() => {
                                                setViewerIndex(i + 1);
                                                setViewerOpen(true);
                                            }}
                                            className="h-24 w-full cursor-pointer object-cover sm:h-28"
                                            width={800}
                                            height={450}
                                        />
                                    )}
                                    {i === thumbUrls.length - 1 &&
                                    remainingThumbs > 0 ? (
                                        <div
                                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white text-sm font-semibold cursor-pointer"
                                            onClick={() => {
                                                setViewerIndex(
                                                    thumbUrls.length,
                                                );
                                                setViewerOpen(true);
                                            }}
                                        >
                                            +{remainingThumbs} more
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    <div className="rounded-3xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] p-6">
                        <div className="text-sm font-semibold">Overview</div>
                        <p className="mt-2 text-sm leading-relaxed text-[rgb(var(--muted2))]">
                            {property.description}
                        </p>

                        <div className="mt-4 flex flex-wrap gap-2">
                            {property.tags.map((t) => (
                                <Badge key={t}>{t}</Badge>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-4 lg:col-span-2">
                    <div className="rounded-3xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] p-6">
                        <div className="text-sm font-semibold">Key details</div>

                        <div className="mt-4 grid gap-3 text-sm">
                            {keyDetailsRows.map((row) => (
                                <div
                                    key={row.label}
                                    className="flex items-center justify-between rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.40)] px-4 py-3"
                                >
                                    <div className="text-[rgb(var(--muted))]">
                                        {row.label}
                                    </div>
                                    <div className="font-semibold">
                                        {row.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] p-6">
                        <div className="text-sm font-semibold">Features</div>
                        <ul className="mt-3 space-y-2 text-sm text-[rgb(var(--muted2))]">
                            {property.features.map((f) => (
                                <li key={f} className="flex items-start gap-3">
                                    <span className="mt-[0.48em] h-2 w-1 shrink-0 rounded bg-[rgb(var(--accent)/0.55)]" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {!isStandalone && (
                        <Link
                            to="/properties"
                            className="inline-flex w-full items-center justify-center rounded-2xl border border-[rgb(var(--border)/0.15)] bg-[rgb(var(--surface)/0.05)] px-4 py-3 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgb(var(--surface)/0.10)]"
                        >
                            Back to properties
                        </Link>
                    )}
                </div>
            </div>

            {viewerOpen && !isVideos ? (
                <GalleryViewer
                    urls={currentUrls.length > 0 ? currentUrls : [imageUrl]}
                    startIndex={viewerIndex}
                    title={property.title}
                    onClose={() => setViewerOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default PropertyDetailPage;
